/**
 * HROS Dashboard — KPI cards + attendance trend + leave distribution +
 * upcoming birthdays/anniversaries + department headcount + hiring trend.
 *
 * Endpoints:
 *   GET /v1/hros/dashboard/summary            — 5 core KPIs
 *   GET /v1/hros/dashboard/attendance-trend   — 30-day area chart
 *   GET /v1/hros/dashboard/leave-distribution — pie chart by leave type
 *   GET /v1/hros/dashboard/upcoming-events    — birthdays & anniversaries (next 30 days)
 *   GET /v1/hros/dashboard/department-headcount — bar chart by dept
 *   GET /v1/hros/dashboard/hiring-trend       — monthly new hires (last 6 months)
 */

import * as React from 'react';
import { useNavigate } from 'react-router';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  KpiCard,
  Button,
  ChartContainer,
  Badge,
} from '@yesbos/ui';
import {
  Users,
  Calendar,
  CheckSquare,
  Target,
  Clock,
  Plus,
  UserX,
  Cake,
  Star,
  TrendingUp,
  Building2,
} from 'lucide-react';
import { hrosApi } from '@/lib/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DashboardSummary {
  headcount: number;
  on_leave_today: number;
  present_today: number;
  pending_approvals: number;
  goals_on_track: number;
  as_of: string;
}

interface AttendanceTrendPoint {
  date: string;
  present: number;
  absent: number;
  on_leave: number;
}

interface LeaveDistributionItem {
  leave_type: string;
  count: number;
  color: string;
}

interface UpcomingEvent {
  employee_id: string;
  name: string;
  type: 'birthday' | 'anniversary';
  date: string;
  days_away: number;
  years?: number;
}

interface DeptHeadcount {
  department: string;
  headcount: number;
}

interface HiringTrendPoint {
  month: string;
  new_hires: number;
}

// ── Fetch helper ──────────────────────────────────────────────────────────────

async function apiFetch<T>(path: string): Promise<T> {
  const res = await hrosApi.get<T>(path);
  if (!res.success) throw new Error(res.error?.message ?? `Failed: ${path}`);
  return res.data as T;
}

// ── Upcoming Events Widget ────────────────────────────────────────────────────

function UpcomingEventsWidget({ events }: { events: UpcomingEvent[] }) {
  if (events.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-6">
        No upcoming birthdays or anniversaries in the next 30 days.
      </p>
    );
  }

  return (
    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
      {events.map((ev) => (
        <div
          key={`${ev.employee_id}-${ev.type}-${ev.date}`}
          className="flex items-center gap-3 rounded-md p-2 hover:bg-muted/50 transition-colors"
        >
          <div
            className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${
              ev.type === 'birthday'
                ? 'bg-pink-100 text-pink-600'
                : 'bg-amber-100 text-amber-600'
            }`}
          >
            {ev.type === 'birthday' ? (
              <Cake className="h-4 w-4" />
            ) : (
              <Star className="h-4 w-4" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{ev.name}</p>
            <p className="text-xs text-muted-foreground">
              {ev.type === 'anniversary'
                ? `${ev.years} year${ev.years !== 1 ? 's' : ''} work anniversary`
                : 'Birthday'}
              {' · '}
              {new Date(ev.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
            </p>
          </div>
          <Badge
            variant={ev.days_away === 0 ? 'default' : 'secondary'}
            className="text-xs shrink-0"
          >
            {ev.days_away === 0 ? 'Today' : `${ev.days_away}d`}
          </Badge>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function DashboardPage() {
  const navigate = useNavigate();

  const [summary, setSummary] = React.useState<DashboardSummary | null>(null);
  const [trend, setTrend] = React.useState<AttendanceTrendPoint[]>([]);
  const [distribution, setDistribution] = React.useState<LeaveDistributionItem[]>([]);
  const [events, setEvents] = React.useState<UpcomingEvent[]>([]);
  const [deptHeadcount, setDeptHeadcount] = React.useState<DeptHeadcount[]>([]);
  const [hiringTrend, setHiringTrend] = React.useState<HiringTrendPoint[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sumData, trendData, distData, eventsData, deptData, hiringData] = await Promise.all([
        apiFetch<DashboardSummary>('/v1/hros/dashboard/summary'),
        apiFetch<AttendanceTrendPoint[]>('/v1/hros/dashboard/attendance-trend?days=30'),
        apiFetch<LeaveDistributionItem[]>('/v1/hros/dashboard/leave-distribution'),
        apiFetch<UpcomingEvent[]>('/v1/hros/dashboard/upcoming-events?days=30'),
        apiFetch<DeptHeadcount[]>('/v1/hros/dashboard/department-headcount'),
        apiFetch<HiringTrendPoint[]>('/v1/hros/dashboard/hiring-trend?months=6'),
      ]);
      setSummary(sumData);
      setTrend(trendData);
      setDistribution(distData);
      setEvents(eventsData);
      setDeptHeadcount(deptData);
      setHiringTrend(hiringData);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const now = new Date();
  const dateString = now.toLocaleDateString('en-IN', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const todayEventCount = events.filter((e) => e.days_away === 0).length;

  return (
    <div className="space-y-6 p-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-[var(--color-primary)]/10 to-[var(--color-primary)]/5 border border-[var(--color-primary)]/20 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">HR Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">{dateString}</p>
          </div>
          {todayEventCount > 0 && (
            <div className="text-right">
              <p className="text-sm font-medium text-primary">
                🎉 {todayEventCount} celebration{todayEventCount > 1 ? 's' : ''} today!
              </p>
              <p className="text-xs text-muted-foreground">Check upcoming events below</p>
            </div>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-28 rounded-lg border bg-muted animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive flex items-center gap-2">
          <span>{error}</span>
          <Button size="sm" variant="outline" onClick={load}>Retry</Button>
        </div>
      ) : summary ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <KpiCard
            title="Total Employees"
            value={summary.headcount}
            icon={<Users className="h-5 w-5" />}
            subtitle="Active headcount"
            color="primary"
          />
          <KpiCard
            title="Present Today"
            value={summary.present_today}
            icon={<CheckSquare className="h-5 w-5" />}
            subtitle="Checked in"
            color="success"
          />
          <KpiCard
            title="On Leave Today"
            value={summary.on_leave_today}
            icon={<UserX className="h-5 w-5" />}
            subtitle="Approved leave"
            color="warning"
          />
          <KpiCard
            title="Pending Approvals"
            value={summary.pending_approvals}
            icon={<Calendar className="h-5 w-5" />}
            subtitle="Awaiting action"
            color={summary.pending_approvals > 0 ? 'warning' : 'success'}
          />
          <KpiCard
            title="Goals On Track"
            value={summary.goals_on_track}
            icon={<Target className="h-5 w-5" />}
            subtitle="Active goals"
            color="primary"
          />
        </div>
      ) : null}

      {/* Pending approvals banner */}
      {summary && summary.pending_approvals > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/40">
          <CardContent className="flex items-center justify-between py-4">
            <div>
              <p className="font-medium">
                {summary.pending_approvals} leave request{summary.pending_approvals !== 1 ? 's' : ''} awaiting approval
              </p>
              <p className="text-sm text-muted-foreground">Review and action them now</p>
            </div>
            <Button onClick={() => navigate('/leave')}>Review Requests</Button>
          </CardContent>
        </Card>
      )}

      {/* Charts Row 1: Attendance + Leave Distribution */}
      {!loading && !error && (
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Attendance Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <CheckSquare className="h-4 w-4 text-muted-foreground" />
                Attendance — Last 30 Days
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trend.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No attendance data yet</p>
              ) : (
                <ChartContainer
                  type="area"
                  data={trend}
                  series={[
                    { dataKey: 'present', name: 'Present', color: '#16a34a' },
                    { dataKey: 'on_leave', name: 'On Leave', color: '#ca8a04' },
                    { dataKey: 'absent', name: 'Absent', color: '#dc2626' },
                  ]}
                  xAxisKey="date"
                  height={220}
                  xAxisFormat="dayMonth"
                />
              )}
            </CardContent>
          </Card>

          {/* Leave Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                Leave by Type — {new Date().getFullYear()}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {distribution.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No leave data yet</p>
              ) : (
                <ChartContainer
                  type="pie"
                  data={distribution.map((d) => ({ name: d.leave_type, value: d.count, color: d.color }))}
                  series={[{ dataKey: 'value', name: 'Leaves' }]}
                  xAxisKey="name"
                  height={220}
                />
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Row 2: Department Headcount + Hiring Trend */}
      {!loading && !error && (
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Department Headcount */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                Headcount by Department
              </CardTitle>
            </CardHeader>
            <CardContent>
              {deptHeadcount.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No department data yet</p>
              ) : (
                <ChartContainer
                  type="bar"
                  data={deptHeadcount}
                  series={[{ dataKey: 'headcount', name: 'Employees', color: '#2563eb' }]}
                  xAxisKey="department"
                  height={220}
                />
              )}
            </CardContent>
          </Card>

          {/* Hiring Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                New Hires — Last 6 Months
              </CardTitle>
            </CardHeader>
            <CardContent>
              {hiringTrend.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No hiring data in this period</p>
              ) : (
                <ChartContainer
                  type="bar"
                  data={hiringTrend}
                  series={[{ dataKey: 'new_hires', name: 'New Hires', color: '#16a34a' }]}
                  xAxisKey="month"
                  height={220}
                />
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bottom Row: Upcoming Events + Quick Actions */}
      {!loading && !error && (
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Upcoming Birthdays & Anniversaries */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Cake className="h-4 w-4 text-muted-foreground" />
                  Upcoming Events — Next 30 Days
                </span>
                {events.length > 0 && (
                  <Badge variant="secondary">{events.length}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <UpcomingEventsWidget events={events} />
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 grid-cols-2">
                <Button variant="outline" className="justify-start" onClick={() => navigate('/employees/create')}>
                  <Plus className="h-4 w-4 mr-2" /> Add Employee
                </Button>
                <Button variant="outline" className="justify-start" onClick={() => navigate('/leave/create')}>
                  <Calendar className="h-4 w-4 mr-2" /> Apply Leave
                </Button>
                <Button variant="outline" className="justify-start" onClick={() => navigate('/attendance')}>
                  <CheckSquare className="h-4 w-4 mr-2" /> Check In / Out
                </Button>
                <Button variant="outline" className="justify-start" onClick={() => navigate('/time-tracker/create')}>
                  <Clock className="h-4 w-4 mr-2" /> Log Time
                </Button>
                <Button variant="outline" className="justify-start" onClick={() => navigate('/employees')}>
                  <Users className="h-4 w-4 mr-2" /> View Directory
                </Button>
                <Button variant="outline" className="justify-start" onClick={() => navigate('/leave')}>
                  <Target className="h-4 w-4 mr-2" /> Leave Approvals
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
